import { useMemo, useState } from 'react';
import type * as React from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { CardSkeleton, EmptyState, ErrorState, TableSkeleton } from '@/components/states';
import { cn } from '@/lib/utils';

export interface Column<T> {
  key: string;
  header: string;
  /** Numbers right-align and use tabular figures; codes render mono. */
  align?: 'left' | 'right';
  width?: string;
  render: (row: T) => React.ReactNode;
  /** Return a comparable value to make the column sortable. */
  sortValue?: (row: T) => string | number;
  /** Hidden below this breakpoint so a laptop still reads cleanly. */
  hideBelow?: 'md' | 'lg' | 'xl';
}

export interface DataTableProps<T> {
  columns: Array<Column<T>>;
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  empty: React.ReactNode;
  /** Caption for assistive technology; the visible title lives in the page header. */
  label: string;
  /** Rendered under the last row — cursor pagination, totals, footnotes. */
  footer?: React.ReactNode;
  endpoint?: string;
}

const HIDE_CLASS: Record<NonNullable<Column<unknown>['hideBelow']>, string> = {
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
  xl: 'hidden xl:table-cell',
};

/**
 * The table every list screen uses. Sorting is client-side over the page in
 * hand and deliberately so: the API is cursor-paginated with a fixed order
 * (CLAUDE.md §5) and accepts no sort parameter, so a header that claimed to
 * sort the whole collection would be lying about what it did.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  isLoading,
  error,
  onRetry,
  empty,
  label,
  footer,
  endpoint,
}: DataTableProps<T>): React.JSX.Element {
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);

  const sorted = useMemo(() => {
    if (!rows || !sort) return rows;
    const column = columns.find((c) => c.key === sort.key);
    if (!column?.sortValue) return rows;

    const getValue = column.sortValue;
    return [...rows].sort((a, b) => {
      const left = getValue(a);
      const right = getValue(b);
      const cmp =
        typeof left === 'number' && typeof right === 'number'
          ? left - right
          : String(left).localeCompare(String(right));
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sort, columns]);

  const shell = 'overflow-hidden rounded-card border border-line bg-surface';

  if (error) {
    return (
      <div className={shell}>
        <ErrorState error={error} onRetry={onRetry} endpoint={endpoint} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={shell}>
        <TableSkeleton columns={columns.length} />
      </div>
    );
  }

  if (!sorted || sorted.length === 0) {
    return <div className={shell}>{empty}</div>;
  }

  return (
    <div className={shell}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">{label}</caption>
          <thead>
            <tr className="border-b border-line bg-surface-sunken/60">
              {columns.map((column) => {
                const sortable = Boolean(column.sortValue);
                const active = sort?.key === column.key;
                return (
                  <th
                    key={column.key}
                    scope="col"
                    style={column.width ? { width: column.width } : undefined}
                    className={cn(
                      'px-4 py-2.5 text-2xs font-semibold tracking-wider text-ink-soft uppercase',
                      column.align === 'right' ? 'text-right' : 'text-left',
                      column.hideBelow ? HIDE_CLASS[column.hideBelow] : undefined,
                    )}
                    aria-sort={
                      active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined
                    }
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() =>
                          setSort((prev) =>
                            prev?.key === column.key
                              ? { key: column.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                              : { key: column.key, dir: 'asc' },
                          )
                        }
                        className={cn(
                          'inline-flex items-center gap-1 rounded transition-colors hover:text-ink',
                          column.align === 'right' ? 'flex-row-reverse' : undefined,
                          active ? 'text-ink' : undefined,
                        )}
                      >
                        {column.header}
                        {active ? (
                          sort.dir === 'asc' ? (
                            <ArrowUp className="size-3" aria-hidden="true" />
                          ) : (
                            <ArrowDown className="size-3" aria-hidden="true" />
                          )
                        ) : (
                          <ChevronsUpDown className="size-3 opacity-40" aria-hidden="true" />
                        )}
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {sorted.map((row) => (
              <tr key={rowKey(row)} className="transition-colors hover:bg-surface-sunken/50">
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn(
                      'px-4 py-3 align-middle',
                      column.align === 'right' ? 'text-right' : 'text-left',
                      column.hideBelow ? HIDE_CLASS[column.hideBelow] : undefined,
                    )}
                    data-numeric={column.align === 'right' ? '' : undefined}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footer ? (
        <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3 text-xs text-ink-soft">
          {footer}
        </div>
      ) : null}
    </div>
  );
}

export { CardSkeleton, EmptyState };
